import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Send as SendIcon,
  NotificationsActive as AlertIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Groups as ScheduledIcon,
  CheckCircle as PresentIcon,
  Schedule as LateIcon,
  PersonOff as AbsentIcon,
  SwapHoriz as CoveredIcon,
  WarningAmber as UncoveredIcon,
  Speed as ReadinessIcon,
  Hub as LoadIcon,
  KeyboardArrowDown as ExpandSectionIcon,
  KeyboardArrowUp as CollapseSectionIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Campaign, User } from '../types';
import { CampaignFilterService } from '../services/campaignFilterService';
import CoverageAssignModal, { CoverageFormState } from '../components/coverage/CoverageAssignModal';
import AgentTimelineModal from '../components/coverage/AgentTimelineModal';
import CoverageKanbanView from '../components/coverage/CoverageKanbanView';
import SendSummaryModal from '../components/coverage/SendSummaryModal';
import ManagerAlertModal from '../components/coverage/ManagerAlertModal';
import CampaignOperationsBoard from '../components/coverage/CampaignOperationsBoard';
import CommandCenterPanel from '../components/coverage/CommandCenterPanel';
import AttendanceGridView from '../components/coverage/AttendanceGridView';
import EmployeeDetailDrawer from '../components/coverage/EmployeeDetailDrawer';
import BackToTopFab from '../components/coverage/BackToTopFab';
import KpiStatCard from '../components/coverage/KpiStatCard';
import CompactSummaryBar from '../components/coverage/CompactSummaryBar';
import RosterInsightsPanel from '../components/coverage/RosterInsightsPanel';
import CampaignPerformanceStrip from '../components/coverage/CampaignPerformanceStrip';
import CompactOperationsFooter from '../components/coverage/CompactOperationsFooter';
import DashboardFilterToolbar from '../components/coverage/DashboardFilterToolbar';
import {
  applyCoverageStatusToForm,
  canShowAddCoverageMenuItem,
  canShowAssignCoverageMenuItem,
  canShowMarkCoverageNotRequired,
  coverageStatusRequiresCoveringAgent,
  normalizeTimeForInput,
  timeOptionFromCoverageStatus,
} from '../utils/coverageDashboardUtils';
import AttendanceTimingLegend from '../components/AttendanceTimingLegend';
import {
  DashboardDensity,
  getDashboardDensity,
  isCompact,
  setDashboardDensity,
} from '../utils/dashboardDensity';
import { OPS_COLORS } from '../constants/operationsDashboardColors';
import {
  ALL_FILTER,
  DASHBOARD_FILTER_DEFAULTS,
  toApiFilterId,
} from '../constants/dashboardDefaults';
import { parseApiBlobError } from '../utils/parseApiBlobError';
import {
  ActionQueueItem,
  buildActionQueue,
  computeWeightedOperationalReadiness,
  formatStaffingValue,
  getCoverageLoadTooltip,
  buildReadinessTooltip,
} from '../utils/operationsDashboardUtils';
import { RosterMetrics } from '../utils/rosterMetrics';
import { countDistinctEmployees } from '../utils/employeeDashboardGrouping';
import {
  filterRowsByKpi,
  getKpiFilterLabel,
  KpiFilterKey,
  toggleKpiFilter,
} from '../utils/kpiDashboardFilters';
import WatchTutorialLink from '../components/help/WatchTutorialLink';

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const EMPLOYEES_SECTION_STORAGE_PREFIX = 'teks-time-employees-section-open';

function getEmployeesSectionOpen(userId?: string): boolean {
  try {
    const key = userId ? `${EMPLOYEES_SECTION_STORAGE_PREFIX}-${userId}` : EMPLOYEES_SECTION_STORAGE_PREFIX;
    return localStorage.getItem(key) !== 'false';
  } catch {
    return true;
  }
}

function persistEmployeesSectionOpen(userId: string | undefined, open: boolean): void {
  try {
    const key = userId ? `${EMPLOYEES_SECTION_STORAGE_PREFIX}-${userId}` : EMPLOYEES_SECTION_STORAGE_PREFIX;
    localStorage.setItem(key, String(open));
  } catch {
    // ignore storage errors
  }
}

const today = () => new Date().toISOString().split('T')[0];

const DailyAttendanceCoveragePage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [date, setDate] = useState(today());
  const [campaignId, setCampaignId] = useState<string>(DASHBOARD_FILTER_DEFAULTS.campaignId);
  const [supervisorId, setSupervisorId] = useState<string>(DASHBOARD_FILTER_DEFAULTS.supervisorId);
  const [attendanceStatus, setAttendanceStatus] = useState<string>(DASHBOARD_FILTER_DEFAULTS.attendanceStatus);
  const [coverageStatus, setCoverageStatus] = useState<string>(DASHBOARD_FILTER_DEFAULTS.coverageStatus);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [supervisors, setSupervisors] = useState<User[]>([]);
  const [employees, setEmployees] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewTab, setViewTab] = useState<'grid' | 'kanban'>('grid');
  const [sendSummaryOpen, setSendSummaryOpen] = useState(false);
  const [managerAlertOpen, setManagerAlertOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'assign' | 'change' | 'note' | 'remove' | 'not_required'>('assign');
  const [saving, setSaving] = useState(false);
  const [autoRefreshMs, setAutoRefreshMs] = useState<number>(DASHBOARD_FILTER_DEFAULTS.autoRefreshMs);
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineEmployee, setTimelineEmployee] = useState<{ id: string; name: string } | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRow, setDrawerRow] = useState<any>(null);
  const [density, setDensity] = useState<DashboardDensity>(() => getDashboardDensity(user?.id));
  const [groupByCampaign, setGroupByCampaign] = useState(true);
  const [employeesSectionOpen, setEmployeesSectionOpen] = useState(() => getEmployeesSectionOpen(user?.id));
  const [kpiFilter, setKpiFilter] = useState<KpiFilterKey>('scheduled');
  const [coverageScope, setCoverageScope] = useState<'assigned' | 'all_coverage'>('assigned');

  useEffect(() => {
    if (user?.id) setDensity(getDashboardDensity(user.id));
  }, [user?.id]);

  const handleDensityChange = (_: React.MouseEvent<HTMLElement>, value: DashboardDensity | null) => {
    if (!value) return;
    setDensity(value);
    setDashboardDensity(user?.id, value);
    if (value === 'compact') setViewTab('grid');
  };

  const compact = isCompact(density);
  const [coverageForm, setCoverageForm] = useState<CoverageFormState>({
    absentEmployeeId: '',
    absentAgentName: '',
    campaignId: '',
    campaignName: '',
    workDate: today(),
    scheduledStartTime: '',
    scheduledEndTime: '',
    attendanceStatus: '',
    currentCoverageStatus: '',
    absenceReason: 'PENDING_SUPERVISOR_REVIEW',
    coveringEmployeeId: '',
    coverageType: 'FULL_SHIFT',
    coverageReason: 'FULL_SHIFT_COVERAGE',
    coverageStartTime: '',
    coverageEndTime: '',
    coverageMode: 'SHARED_COVERAGE',
    coverageStatus: 'COVERED',
    notes: '',
    customerNotified: false,
  });

  // Prefer permission over role name for manage actions.
  const canManage = useMemo(() => {
    if (!user) return false;
    if (user.role === 'hr') return false;
    return (user.permissions || []).includes('coverage_assignments.create');
  }, [user]);

  const canNotify = useMemo(() => (user?.permissions || []).includes('operations.attendance_coverage.notify'), [user]);
  const canCrossCampaignScope = useMemo(() => {
    const perms = user?.permissions || [];
    return perms.includes('operations.attendance_coverage.cross_campaign_read')
      || perms.includes('operations.attendance_coverage.cross_campaign_assign');
  }, [user]);
  const canExport = useMemo(() => {
    if (!user) return false;
    if (user.role === 'hr') return (user.permissions || []).includes('operations.attendance_coverage.export');
    return (user.permissions || []).includes('operations.attendance_coverage.read');
  }, [user]);

  const loadFilters = useCallback(async () => {
    try {
      const [campaignRes, userRes] = await Promise.all([
        apiClient.campaigns.getAll({
          includeWithActivityFrom: date,
          includeWithActivityTo: date,
        }),
        apiClient.users.getAll(),
      ]);
      const allCampaigns = campaignRes.data.data || [];
      // When All Coverage Needs is active, show all campaigns returned for filter context from dashboard rows later.
      const accessible = user && coverageScope !== 'all_coverage'
        ? CampaignFilterService.getUserAccessibleCampaigns(user, allCampaigns)
        : allCampaigns;
      setCampaigns(accessible);
      const allUsers = userRes.data.data || [];
      setEmployees(allUsers.filter((u: User) => u.isActive));
      setSupervisors(allUsers.filter((u: User) => ['supervisor', 'team_lead', 'manager'].includes(u.role)));
    } catch (err) {
      console.error(err);
    }
  }, [user, date, coverageScope]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const { data, isLoading, refetch, isFetching, dataUpdatedAt, isError, error: queryError } = useQuery({
    queryKey: ['attendance-coverage', date, campaignId, supervisorId, attendanceStatus, coverageStatus, coverageScope],
    queryFn: async () => {
      const response = await apiClient.operations.getAttendanceCoverage({
        date,
        campaignId: toApiFilterId(campaignId),
        supervisorId: toApiFilterId(supervisorId),
        attendanceStatus: attendanceStatus === 'all' ? undefined : attendanceStatus,
        coverageStatus: coverageStatus === 'all' ? undefined : coverageStatus,
        coverageScope: canCrossCampaignScope ? coverageScope : 'assigned',
      });
      return response.data.data;
    },
    refetchInterval: modalOpen ? false : (autoRefreshMs || false),
  });

  const loadError = isError
    ? ((queryError as any)?.response?.data?.message || 'Failed to load attendance coverage dashboard.')
    : null;

  const summary = data?.summaryCards;
  const rows = data?.rows || [];
  const campaignSummary = data?.campaignSummary || [];
  const rosterMetrics = data?.rosterMetrics as RosterMetrics | undefined;

  // When viewing All Coverage Needs, enrich campaign filter with campaigns present in the queue.
  useEffect(() => {
    if (coverageScope !== 'all_coverage' || !campaignSummary.length) return;
    setCampaigns((prev) => {
      const byId = new Map(prev.map((c) => [c.id, c]));
      campaignSummary.forEach((item: { campaignId: string; campaignName: string }) => {
        if (item.campaignId && !byId.has(item.campaignId)) {
          byId.set(item.campaignId, {
            id: item.campaignId,
            name: item.campaignName,
            isActive: true,
          } as Campaign);
        }
      });
      return Array.from(byId.values());
    });
  }, [coverageScope, campaignSummary]);

  const filteredRows = useMemo(
    () => filterRowsByKpi(rows, kpiFilter, campaignSummary),
    [rows, kpiFilter, campaignSummary],
  );

  const kpiFilterLabel = useMemo(() => getKpiFilterLabel(kpiFilter), [kpiFilter]);

  const handleKpiFilterClick = useCallback((filter: KpiFilterKey) => {
    setKpiFilter((current) => toggleKpiFilter(current, filter));
    setEmployeesSectionOpen(true);
    persistEmployeesSectionOpen(user?.id, true);
    setViewTab('grid');
  }, [user?.id]);
  const alerts = data?.alerts || [];
  const operationalHealth = data?.operationalHealth;
  const dailyNotes = data?.dailyNotes || [];
  const lastUpdated = data?.dataUpdatedAt || (dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : undefined);

  const operationalReadiness = useMemo(
    () => computeWeightedOperationalReadiness(campaignSummary),
    [campaignSummary],
  );

  const actionQueue = useMemo(
    () => buildActionQueue(rows, campaignSummary),
    [rows, campaignSummary],
  );

  const employeeCount = useMemo(() => countDistinctEmployees(filteredRows), [filteredRows]);

  const toggleEmployeesSection = useCallback(() => {
    setEmployeesSectionOpen((prev) => {
      const next = !prev;
      persistEmployeesSectionOpen(user?.id, next);
      return next;
    });
  }, [user?.id]);

  const coveringAgentCount = useMemo(
    () => new Set(rows.filter((row: any) => row.isCoveringOthers).map((row: any) => row.employeeId)).size,
    [rows],
  );

  const findRowForAlert = (alert: any) => {
    if (alert.rowId) return rows.find((r: any) => r.id === alert.rowId);
    if (alert.employeeId) return rows.find((r: any) => r.employeeId === alert.employeeId && r.campaignId === alert.campaignId);
    return null;
  };

  const scrollToRow = (row: any) => {
    if (!row) return;
    setViewTab('grid');
    setEmployeesSectionOpen(true);
    persistEmployeesSectionOpen(user?.id, true);
    const targetId = row.employeeId || row.id;
    setHighlightedRowId(targetId);
    setTimeout(() => {
      rowRefs.current[targetId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    setTimeout(() => setHighlightedRowId(null), 3000);
  };

  const handleCampaignSelect = (id: string) => {
    setCampaignId((prev) => (prev === id ? ALL_FILTER : id));
  };

  const findCampaignCoverageRow = (targetCampaignId: string) => rows.find(
    (r: any) =>
      r.campaignId === targetCampaignId
      && r.coverageAssignmentId
      && ['COVERED', 'PARTIALLY_COVERED'].includes(r.coverageStatus),
  );

  const handleAssignCoverageForCampaign = (targetCampaignId: string) => {
    const uncovered = rows.find(
      (r: any) => r.campaignId === targetCampaignId && r.coverageStatus === 'NOT_COVERED' && r.requiresCoverage,
    );
    if (uncovered) {
      openCoverageModal(uncovered, uncovered.coverageAssignmentId ? 'change' : 'assign');
      scrollToRow(uncovered);
      return;
    }
    const partial = rows.find(
      (r: any) => r.campaignId === targetCampaignId && r.coverageStatus === 'PARTIALLY_COVERED',
    );
    if (partial) {
      openCoverageModal(partial, 'assign');
      scrollToRow(partial);
      return;
    }
    toast.info('No uncovered positions in this campaign for the current filters.');
  };

  const handleChangeCoverageForCampaign = (targetCampaignId: string) => {
    const row = findCampaignCoverageRow(targetCampaignId);
    if (row) {
      openCoverageModal(row, 'change');
      scrollToRow(row);
    } else {
      toast.info('No active coverage assignment in this campaign.');
    }
  };

  const handleRemoveCoverageForCampaign = (targetCampaignId: string) => {
    const row = findCampaignCoverageRow(targetCampaignId);
    if (row) {
      openCoverageModal(row, 'remove');
      scrollToRow(row);
    } else {
      toast.info('No active coverage assignment in this campaign.');
    }
  };

  const handleActionQueueItem = (item: ActionQueueItem) => {
    if (item.campaignId) setCampaignId(item.campaignId);
    if (item.rowId) {
      const row = rows.find((r: any) => r.id === item.rowId);
      if (!row) return;
      if (item.actionType === 'assign_coverage' || item.actionType === 'coverage_pending') {
        openCoverageModal(row, row.coverageAssignmentId ? 'change' : 'assign');
      } else if (item.actionType === 'review_absence') {
        openCoverageModal(row, 'note');
      }
      scrollToRow(row);
      return;
    }
    if (item.actionType === 'staffing_threshold' && item.campaignId) {
      handleAssignCoverageForCampaign(item.campaignId);
    }
  };

  const openEmployeeDrawer = (row: any) => {
    setDrawerRow(row);
    setDrawerOpen(true);
  };

  const openCoverageModal = (row: any, mode: 'assign' | 'change' | 'note' | 'remove' | 'not_required') => {
    setSelectedRow(row);
    setModalMode(mode);
    const scheduledStartTime = normalizeTimeForInput(row.scheduledStartTime || '');
    const scheduledEndTime = normalizeTimeForInput(row.scheduledEndTime || '');
    const existingStartTime = normalizeTimeForInput(row.coverageStartTime || row.scheduledStartTime || '');
    const existingEndTime = normalizeTimeForInput(row.coverageEndTime || row.scheduledEndTime || '');
    const addingAnotherPartial = mode === 'assign' && canShowAddCoverageMenuItem(row);
    let coverageStatus = row.coverageStatus === 'NOT_REQUIRED' ? 'COVERED' : (row.coverageStatus || 'COVERED');
    if (mode === 'assign' && coverageStatus === 'NOT_COVERED') {
      coverageStatus = 'COVERED';
    }
    if (addingAnotherPartial) {
      coverageStatus = 'PARTIALLY_COVERED';
    }
    const coverageMode = row.coverageMode || 'SHARED_COVERAGE';
    // When adding a second partial, suggest the remainder after the latest covered end.
    let suggestedStart = existingStartTime;
    let suggestedEnd = existingEndTime;
    if (addingAnotherPartial && existingEndTime && scheduledEndTime && existingEndTime < scheduledEndTime) {
      const [eh, em] = existingEndTime.split(':').map(Number);
      const nextMin = eh * 60 + em + 1;
      const nh = Math.floor(nextMin / 60);
      const nm = nextMin % 60;
      suggestedStart = `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
      suggestedEnd = scheduledEndTime;
    }
    const baseForm = {
      absentEmployeeId: row.employeeId,
      absentAgentName: row.agentName,
      campaignId: row.campaignId,
      campaignName: row.campaignName,
      workDate: date,
      scheduledStartTime,
      scheduledEndTime,
      attendanceStatus: row.attendanceStatus || '',
      currentCoverageStatus: row.coverageStatus || '',
      absenceReason: row.absenceReason || 'PENDING_SUPERVISOR_REVIEW',
      coveringEmployeeId: addingAnotherPartial ? '' : (row.coveredById || ''),
      coverageStartTime: addingAnotherPartial ? suggestedStart : existingStartTime,
      coverageEndTime: addingAnotherPartial ? suggestedEnd : existingEndTime,
      coverageMode,
      coverageStatus,
      notes: addingAnotherPartial ? '' : (row.notes || ''),
      customerNotified: addingAnotherPartial ? false : Boolean(row.customerNotified),
      coverageAssignmentId: addingAnotherPartial ? null : row.coverageAssignmentId,
      coveredByName: addingAnotherPartial ? '' : row.coveredByName,
      coveringFor: row.coveringFor || [],
      coverageType: addingAnotherPartial ? 'PARTIAL_SHIFT' : (row.coverageType || 'FULL_SHIFT'),
      coverageReason: addingAnotherPartial ? 'PARTIAL_SHIFT_COVERAGE' : (row.coverageReason || 'FULL_SHIFT_COVERAGE'),
    };

    setCoverageForm(applyCoverageStatusToForm(baseForm, coverageStatus, {
      keepPartialTimes: coverageStatus === 'PARTIALLY_COVERED',
    }));
    setModalOpen(true);
    setActionAnchor(null);
  };

  const handleSaveCoverage = async (form: CoverageFormState) => {
    try {
      setSaving(true);
      setError(null);

      if (modalMode === 'remove') {
        if (!form.coverageAssignmentId) throw new Error('No coverage assignment to remove');
        await apiClient.operations.deleteCoverageAssignment(form.coverageAssignmentId, {
          notes: form.notes,
          workDate: form.workDate,
          absentEmployeeId: form.absentEmployeeId,
        });
        toast.success('Coverage removed');
        setModalOpen(false);
        await queryClient.invalidateQueries({ queryKey: ['attendance-coverage'] });
        return;
      }

      if (modalMode === 'not_required') {
        if (form.coverageAssignmentId) {
          await apiClient.operations.markCoverageNotRequired(form.coverageAssignmentId, {
            notes: form.notes,
            workDate: form.workDate,
            absentEmployeeId: form.absentEmployeeId,
            absenceReason: form.absenceReason,
          });
        } else {
          await apiClient.operations.createCoverageAssignment({
            workDate: form.workDate,
            campaignId: form.campaignId,
            absentEmployeeId: form.absentEmployeeId,
            coverageStatus: 'NOT_REQUIRED',
            absenceReason: form.absenceReason,
            notes: form.notes,
            customerNotified: form.customerNotified,
            scheduledStartTime: form.scheduledStartTime,
            scheduledEndTime: form.scheduledEndTime,
          });
        }
        toast.success('Marked as no coverage needed');
        setModalOpen(false);
        await queryClient.invalidateQueries({ queryKey: ['attendance-coverage'] });
        return;
      }

      if (modalMode === 'assign' || modalMode === 'change') {
        if (coverageStatusRequiresCoveringAgent(form.coverageStatus) && !form.coveringEmployeeId) {
          throw new Error('Select a covering agent in Covered By before saving.');
        }
      }

      const payload = {
        workDate: form.workDate,
        campaignId: form.campaignId,
        absentEmployeeId: form.absentEmployeeId,
        coveringEmployeeId: form.coveringEmployeeId || null,
        coverageType: form.coverageType,
        coverageMode: form.coverageMode,
        timeOption: timeOptionFromCoverageStatus(form.coverageStatus),
        coverageReason: form.coverageReason,
        absenceReason: form.absenceReason,
        coverageStartTime: form.coverageStartTime,
        coverageEndTime: form.coverageEndTime,
        coverageStatus: form.coverageStatus,
        notes: form.notes,
        customerNotified: form.customerNotified,
        scheduledStartTime: form.scheduledStartTime,
        scheduledEndTime: form.scheduledEndTime,
      };

      let response: any;
      if (form.coverageAssignmentId && (modalMode === 'change' || modalMode === 'note')) {
        response = await apiClient.operations.updateCoverageAssignment(form.coverageAssignmentId, payload);
      } else {
        response = await apiClient.operations.createCoverageAssignment(payload);
      }

      if (response.data.notificationWarning) {
        toast.warn(response.data.notificationWarning);
      } else {
        toast.success('Coverage saved successfully');
      }
      if (Array.isArray(response.data.warnings) && response.data.warnings.length > 0) {
        response.data.warnings.forEach((warning: { message: string }) => toast.warn(warning.message));
      }
      setModalOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['attendance-coverage'] });
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to save coverage';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const exportParams = {
    date,
    campaignId: toApiFilterId(campaignId),
    supervisorId: toApiFilterId(supervisorId),
    attendanceStatus: attendanceStatus === 'all' ? undefined : attendanceStatus,
    coverageStatus: coverageStatus === 'all' ? undefined : coverageStatus,
  };

  const handleExportExcel = async () => {
    try {
      const response = await apiClient.operations.exportExcel(exportParams);
      downloadBlob(response.data, `attendance-coverage-${date}.xlsx`);
      toast.success('Excel export downloaded');
    } catch (error: any) {
      toast.error(await parseApiBlobError(error, 'Export failed'));
    }
  };

  const handleExportPdf = async () => {
    try {
      const response = await apiClient.operations.exportPdf(exportParams);
      downloadBlob(response.data, `attendance-coverage-${date}.pdf`);
      toast.success('PDF export downloaded');
    } catch (error: any) {
      toast.error(await parseApiBlobError(error, 'Export failed'));
    }
  };

  const readinessTooltip = buildReadinessTooltip({
    assignmentPositionsRequired: operationalReadiness.requiredStaff,
    availableAtPosition: operationalReadiness.availableStaff,
    coveredPositions: operationalReadiness.coveredPositions,
    uncoveredPositions: operationalReadiness.uncoveredPositions,
  });

  const coverageLoadTooltip = getCoverageLoadTooltip(summary || {});

  return (
    <Container maxWidth={false} sx={{ mt: compact ? 0.5 : 1.5, mb: compact ? 1 : 2, px: { xs: 1, sm: compact ? 1.5 : 2 } }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={0.75}
        sx={{ mb: compact ? 0.5 : 1, minHeight: compact ? 28 : undefined }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {compact ? (
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: '0.95rem' }}>
                Daily Attendance & Coverage
              </Typography>
              <AttendanceTimingLegend compact showNote={false} />
            </Stack>
          ) : (
            <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                Daily Attendance & Coverage
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Operational Command Center
              </Typography>
              <WatchTutorialLink contextKey="attendance-coverage" />
              <AttendanceTimingLegend compact showNote={false} />
            </Stack>
          )}
        </Box>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={density}
            onChange={handleDensityChange}
            sx={{ height: 32, '& .MuiToggleButton-root': { px: 1, py: 0.25, fontSize: '0.7rem' } }}
          >
            <ToggleButton value="standard">Standard</ToggleButton>
            <ToggleButton value="compact">Compact</ToggleButton>
          </ToggleButtonGroup>
          {canNotify && (
            <>
              <Tooltip title="Preview / Send Manager Alert">
                <IconButton size="small" onClick={() => setManagerAlertOpen(true)}><AlertIcon fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title="Send Summary">
                <IconButton size="small" onClick={() => setSendSummaryOpen(true)}><SendIcon fontSize="small" /></IconButton>
              </Tooltip>
            </>
          )}
          {canExport && data && (
            <>
              <Tooltip title="Export Excel">
                <IconButton size="small" onClick={handleExportExcel}><FileDownloadIcon fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title="Export PDF">
                <IconButton size="small" onClick={handleExportPdf}><PictureAsPdfIcon fontSize="small" /></IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1, py: 0 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <DashboardFilterToolbar
        date={date}
        onDateChange={setDate}
        campaignId={campaignId}
        onCampaignChange={setCampaignId}
        supervisorId={supervisorId}
        onSupervisorChange={setSupervisorId}
        attendanceStatus={attendanceStatus}
        onAttendanceChange={setAttendanceStatus}
        coverageStatus={coverageStatus}
        onCoverageChange={setCoverageStatus}
        autoRefreshMs={autoRefreshMs}
        onAutoRefreshChange={setAutoRefreshMs}
        campaigns={campaigns}
        supervisors={supervisors}
        onRefresh={() => refetch()}
        isFetching={isFetching}
        density={density}
        coverageScope={coverageScope}
        onCoverageScopeChange={setCoverageScope}
        canUseCrossCampaignScope={canCrossCampaignScope}
      />

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : loadError ? (
        <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
      ) : (
        <>
          {rosterMetrics && (
            <RosterInsightsPanel metrics={rosterMetrics} compact={compact} />
          )}

          {!data || (rows.length === 0 && (summary?.scheduled ?? 0) === 0) ? (
            <Alert severity="info">No scheduled agents found for the selected date and filters.</Alert>
          ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: compact ? 'column' : 'row' },
              gap: compact ? 0.75 : 1.5,
              alignItems: 'flex-start',
            }}
          >
            <Box sx={{ flex: '1 1 auto', minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column', minHeight: compact ? 'calc(100vh - 120px)' : undefined }}>
              {compact ? (
                <>
                  <CompactSummaryBar
                    scheduled={summary?.scheduled ?? 0}
                    present={summary?.present ?? 0}
                    presentLate={summary?.late ?? 0}
                    absent={summary?.absent ?? 0}
                    covering={coveringAgentCount}
                    uncovered={summary?.uncovered ?? 0}
                    readinessPercent={operationalReadiness.score}
                    coverageLoad={summary?.coverageLoad ?? 'Normal'}
                    activeFilter={kpiFilter}
                    onFilterClick={handleKpiFilterClick}
                  />
                  <CampaignPerformanceStrip
                    campaigns={campaignSummary}
                    selectedCampaignId={campaignId === ALL_FILTER ? '' : campaignId}
                    onSelectCampaign={handleCampaignSelect}
                  />
                </>
              ) : (
                <Box
                  sx={{
                    mb: 1,
                    display: 'grid',
                    gap: 0.75,
                    gridTemplateColumns: {
                      xs: 'repeat(2, 1fr)',
                      sm: 'repeat(4, 1fr)',
                      lg: 'repeat(8, 1fr)',
                    },
                  }}
                >
                  <KpiStatCard
                    density={density}
                    icon={<ScheduledIcon />}
                    label="Scheduled"
                    value={summary?.scheduled ?? 0}
                    color={OPS_COLORS.expected}
                    active={kpiFilter === 'scheduled'}
                    onClick={() => handleKpiFilterClick('scheduled')}
                    tooltip={
                      (summary?.assignmentCount ?? 0) > (summary?.scheduled ?? 0)
                        ? `Unique employees scheduled today. ${summary?.assignmentCount} campaign assignment(s) across shifts.`
                        : 'Unique employees scheduled today (split schedules count once per person).'
                    }
                  />
                  <KpiStatCard
                    density={density}
                    icon={<PresentIcon />}
                    label="Present (on time)"
                    value={summary?.present ?? 0}
                    color={OPS_COLORS.present}
                    active={kpiFilter === 'present'}
                    onClick={() => handleKpiFilterClick('present')}
                    tooltip="Employees clocked in within the grace period."
                  />
                  <KpiStatCard
                    density={density}
                    icon={<LateIcon />}
                    label="Present (Late)"
                    value={summary?.late ?? 0}
                    color={OPS_COLORS.late}
                    active={kpiFilter === 'late'}
                    onClick={() => handleKpiFilterClick('late')}
                    tooltip="Employees clocked in after the grace period. Each person counts once."
                  />
                  <KpiStatCard
                    density={density}
                    icon={<AbsentIcon />}
                    label="Absent"
                    value={summary?.absent ?? 0}
                    color={OPS_COLORS.critical}
                    active={kpiFilter === 'absent'}
                    onClick={() => handleKpiFilterClick('absent')}
                    tooltip={
                      (summary?.noShow ?? 0) > 0
                        ? `Absent or approved leave. No-shows (${summary?.noShow}) are tracked separately.`
                        : 'Absent or approved leave. Each employee counts once.'
                    }
                  />
                  <KpiStatCard
                    density={density}
                    icon={<CoveredIcon />}
                    label="Covered"
                    value={`${summary?.coveredPercent ?? 0}%`}
                    color={OPS_COLORS.coverage}
                    active={kpiFilter === 'covered'}
                    onClick={() => handleKpiFilterClick('covered')}
                    tooltip={`Average fill rate for positions needing coverage. ${formatStaffingValue(summary?.covered ?? 0)} shift equivalent(s) assigned.`}
                  />
                  <KpiStatCard
                    density={density}
                    icon={<UncoveredIcon />}
                    label="Uncovered"
                    value={summary?.uncovered ?? 0}
                    color={OPS_COLORS.uncovered}
                    active={kpiFilter === 'uncovered'}
                    onClick={() => handleKpiFilterClick('uncovered')}
                    tooltip="Campaign assignment positions with no covering agent."
                  />
                  <KpiStatCard
                    density={density}
                    icon={<ReadinessIcon />}
                    label="Readiness"
                    value={`${operationalReadiness.score}%`}
                    color={operationalReadiness.score >= 100 ? OPS_COLORS.excellent : operationalReadiness.score >= 95 ? OPS_COLORS.good : operationalReadiness.score >= 80 ? OPS_COLORS.risk : OPS_COLORS.critical}
                    subtitle={`${operationalReadiness.emoji} ${operationalReadiness.label}`}
                    active={kpiFilter === 'readiness'}
                    onClick={() => handleKpiFilterClick('readiness')}
                    tooltip={readinessTooltip}
                  />
                  <KpiStatCard
                    density={density}
                    icon={<LoadIcon />}
                    label="Load"
                    value={summary?.coverageLoad ?? 'Normal'}
                    color={summary?.coverageLoad === 'Critical' ? OPS_COLORS.critical : summary?.coverageLoad === 'Elevated' ? OPS_COLORS.late : OPS_COLORS.present}
                    active={kpiFilter === 'load'}
                    onClick={() => handleKpiFilterClick('load')}
                    tooltip={coverageLoadTooltip}
                  />
                </Box>
              )}

              {(summary?.coverageLoadWarnings || []).length > 0 && (
                <Alert severity="warning" sx={{ mb: compact ? 0.5 : 0.75, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                  {(summary?.coverageLoadWarnings || []).join(' · ')}
                </Alert>
              )}

              {compact && (
                <CommandCenterPanel
                  operationalHealth={operationalHealth}
                  lastUpdated={lastUpdated}
                  actionQueue={actionQueue}
                  alerts={alerts}
                  rows={rows}
                  dailyNotes={dailyNotes}
                  date={date}
                  campaignId={toApiFilterId(campaignId)}
                  campaigns={campaigns}
                  canManage={canManage}
                  canNotify={canNotify}
                  findRowForAlert={findRowForAlert}
                  onActionQueueItem={handleActionQueueItem}
                  onAssignCoverage={(row) => openCoverageModal(row, row.coverageAssignmentId ? 'change' : 'assign')}
                  onNotRequired={(row) => openCoverageModal(row, 'not_required')}
                  onViewEmployee={(row) => {
                    scrollToRow(row);
                    openEmployeeDrawer(row);
                  }}
                  onRefreshNotes={() => queryClient.invalidateQueries({ queryKey: ['attendance-coverage'] })}
                  onSendSummary={() => setSendSummaryOpen(true)}
                  onRefresh={() => refetch()}
                  density={density}
                />
              )}

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', mb: compact ? 0.5 : 1 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderBottom: employeesSectionOpen ? '1px solid' : 'none',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    bgcolor: employeesSectionOpen ? 'transparent' : 'action.hover',
                  }}
                  onClick={toggleEmployeesSection}
                >
                  <IconButton
                    size="small"
                    aria-label={employeesSectionOpen ? 'Collapse employee section' : 'Expand employee section'}
                    sx={{ p: 0.25 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleEmployeesSection();
                    }}
                  >
                    {employeesSectionOpen
                      ? <CollapseSectionIcon fontSize="small" />
                      : <ExpandSectionIcon fontSize="small" />}
                  </IconButton>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: compact ? '0.85rem' : '0.95rem', lineHeight: 1.3 }}>
                      {compact ? `Employees (${employeeCount})` : 'Employee Attendance'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: compact ? '0.68rem' : '0.72rem' }}>
                      {employeesSectionOpen
                        ? `${employeeCount} employees · ${filteredRows.length} assignments`
                        : `Collapsed — ${employeeCount} employees · ${filteredRows.length} assignments`}
                    </Typography>
                    {kpiFilterLabel && (
                      <Chip
                        size="small"
                        label={`Filter: ${kpiFilterLabel}`}
                        onDelete={() => setKpiFilter('scheduled')}
                        sx={{ mt: 0.5, height: 22, fontSize: '0.68rem' }}
                      />
                    )}
                  </Box>
                  {!compact && employeesSectionOpen && (
                    <Tabs
                      value={viewTab}
                      onChange={(_, value) => setViewTab(value)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        minHeight: 32,
                        '& .MuiTab-root': { minHeight: 32, py: 0.5, fontSize: '0.75rem', fontWeight: 600 },
                      }}
                    >
                      <Tab value="grid" label={`Employees (${employeeCount})`} />
                      <Tab value="kanban" label="Kanban" />
                    </Tabs>
                  )}
                </Stack>
                <Collapse in={employeesSectionOpen} timeout="auto" unmountOnExit>
                  <Box sx={{ p: compact ? 0.25 : 1, flex: compact ? 1 : undefined, minHeight: 0, display: compact ? 'flex' : 'block', flexDirection: 'column' }}>
                    {!compact && viewTab === 'kanban' ? (
                      <CoverageKanbanView rows={filteredRows} />
                    ) : (
                      <AttendanceGridView
                        rows={filteredRows}
                        campaignSummary={campaignSummary}
                        groupByCampaign={groupByCampaign}
                        onToggleGroupByCampaign={setGroupByCampaign}
                        highlightedRowId={highlightedRowId}
                        rowRefs={rowRefs}
                        canManage={canManage}
                        density={density}
                        onRowAction={(row, anchor) => {
                          setSelectedRow(row);
                          setActionAnchor(anchor);
                        }}
                        onRowClick={openEmployeeDrawer}
                        onAssignCoverage={(row) => openCoverageModal(row, row.coverageAssignmentId ? 'change' : 'assign')}
                        onRemoveCoverage={(row) => openCoverageModal(row, 'remove')}
                        onNotRequired={(row) => openCoverageModal(row, 'not_required')}
                        onAddNote={(row) => openCoverageModal(row, 'note')}
                        onViewTimeline={(row) => {
                          setTimelineEmployee({ id: row.employeeId, name: row.agentName });
                          setTimelineOpen(true);
                        }}
                      />
                    )}
                  </Box>
                </Collapse>
              </Box>

              {compact && (
                <CompactOperationsFooter
                  campaignSummary={campaignSummary}
                  actionQueue={actionQueue}
                  rows={rows}
                />
              )}

              {!compact && (
                <CampaignOperationsBoard
                  campaigns={campaignSummary}
                  rows={rows}
                  dailyNotes={dailyNotes}
                  selectedCampaignId={campaignId === ALL_FILTER ? '' : campaignId}
                  onSelectCampaign={handleCampaignSelect}
                  onAssignCoverage={handleAssignCoverageForCampaign}
                  onChangeCoverage={handleChangeCoverageForCampaign}
                  onRemoveCoverage={handleRemoveCoverageForCampaign}
                  canManage={canManage}
                  density={density}
                />
              )}
            </Box>

            {!compact && (
            <Box
              sx={{
                flex: { xs: '1 1 auto', lg: '0 0 19%' },
                width: { xs: '100%', lg: '19%' },
                minWidth: { lg: 240 },
                maxWidth: { lg: '20%' },
              }}
            >
              <CommandCenterPanel
                operationalHealth={operationalHealth}
                lastUpdated={lastUpdated}
                actionQueue={actionQueue}
                alerts={alerts}
                rows={rows}
                dailyNotes={dailyNotes}
                date={date}
                campaignId={toApiFilterId(campaignId)}
                campaigns={campaigns}
                canManage={canManage}
                canNotify={canNotify}
                findRowForAlert={findRowForAlert}
                onActionQueueItem={handleActionQueueItem}
                onAssignCoverage={(row) => openCoverageModal(row, row.coverageAssignmentId ? 'change' : 'assign')}
                onNotRequired={(row) => openCoverageModal(row, 'not_required')}
                onViewEmployee={(row) => {
                  scrollToRow(row);
                  openEmployeeDrawer(row);
                }}
                onRefreshNotes={() => queryClient.invalidateQueries({ queryKey: ['attendance-coverage'] })}
                onSendSummary={() => setSendSummaryOpen(true)}
                onRefresh={() => refetch()}
                density={density}
              />
            </Box>
            )}
          </Box>
        </>
          )}
        </>
      )}

      <Menu anchorEl={actionAnchor} open={Boolean(actionAnchor)} onClose={() => setActionAnchor(null)}>
        {canShowAssignCoverageMenuItem(selectedRow) && (
          <MenuItem onClick={() => openCoverageModal(selectedRow, selectedRow?.coverageAssignmentId ? 'change' : 'assign')}>
            {selectedRow?.coverageAssignmentId ? 'Change Coverage' : 'Assign Coverage'}
          </MenuItem>
        )}
        {canShowAddCoverageMenuItem(selectedRow) && (
          <MenuItem onClick={() => openCoverageModal(selectedRow, 'assign')}>
            Add Coverage
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          setTimelineEmployee({ id: selectedRow.employeeId, name: selectedRow.agentName });
          setTimelineOpen(true);
          setActionAnchor(null);
        }}>
          View Agent Timeline
        </MenuItem>
        <MenuItem onClick={() => openCoverageModal(selectedRow, 'note')}>Add Note</MenuItem>
        {selectedRow?.coverageAssignmentId && (
          <MenuItem onClick={() => openCoverageModal(selectedRow, 'remove')}>Remove Coverage</MenuItem>
        )}
        {canShowMarkCoverageNotRequired(selectedRow) && (
          <MenuItem onClick={() => openCoverageModal(selectedRow, 'not_required')}>Mark No Coverage Needed</MenuItem>
        )}
      </Menu>

      <SendSummaryModal
        open={sendSummaryOpen}
        onClose={() => setSendSummaryOpen(false)}
        date={date}
        campaignId={toApiFilterId(campaignId)}
        supervisorId={toApiFilterId(supervisorId)}
        defaultRecipient={user?.email}
      />

      <ManagerAlertModal
        open={managerAlertOpen}
        onClose={() => setManagerAlertOpen(false)}
        date={date}
        campaignId={toApiFilterId(campaignId)}
        supervisorId={toApiFilterId(supervisorId)}
        defaultRecipient={user?.email}
      />

      <CoverageAssignModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveCoverage}
        form={coverageForm}
        setForm={setCoverageForm}
        loading={saving}
        mode={modalMode}
      />

      <AgentTimelineModal
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
        employeeId={timelineEmployee?.id || ''}
        employeeName={timelineEmployee?.name || ''}
        date={date}
      />

      <EmployeeDetailDrawer
        open={drawerOpen}
        row={drawerRow}
        onClose={() => setDrawerOpen(false)}
        onViewTimeline={(row) => {
          setTimelineEmployee({ id: row.employeeId, name: row.agentName });
          setTimelineOpen(true);
        }}
        onAssignCoverage={(row) => openCoverageModal(row, row.coverageAssignmentId ? 'change' : 'assign')}
        onRemoveCoverage={(row) => openCoverageModal(row, 'remove')}
        canManage={canManage}
      />
      <BackToTopFab />
    </Container>
  );
};

export default DailyAttendanceCoveragePage;
